import { CheckCircle2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../store";
import { clearState, getProfile, register } from "../../../Redux/custmoer_auth/slice";
import { RegisterRequest } from "../../../Redux/custmoer_auth/types";
import {
    fetchAllCountries,
    fetchDistrictsByStateName,
    fetchStatesByCountryName,
} from "../../../Redux/location-mangment/slice";
import { Country as APICountry } from "../../../Redux/location-mangment/types";
import {
    countryOptions as defaultCountryOptions,
    genderOptions,
    passwordRules,
    signUpContent,
    signUpRoutes,
    signUpSocialLinks,
} from "./SignUpData";
import SignUpDatePicker from "./SignUpDatePicker";
import { LOCATION_GATE_SESSION_KEY } from "../../globalcomponents/LocationSetupModal/LocationSetupData";
import SignUpSelect from "./SignUpSelect";
import { SelectOption, SignUpFormErrors, SignUpFormState } from "./SignUpTypes";
import "./SignUpPage.css";

const COUNTRY_DIAL_MAP: Record<string, { dialCode: string; flag: string }> = {
    india: { dialCode: "+91", flag: "🇮🇳" },
    uae: { dialCode: "+971", flag: "🇦🇪" },
    "united arab emirates": { dialCode: "+971", flag: "🇦🇪" },
    usa: { dialCode: "+1", flag: "🇺🇸" },
    "united states": { dialCode: "+1", flag: "🇺🇸" },
    uk: { dialCode: "+44", flag: "🇬🇧" },
    "united kingdom": { dialCode: "+44", flag: "🇬🇧" },
    canada: { dialCode: "+1", flag: "🇨🇦" },
    australia: { dialCode: "+61", flag: "🇦🇺" },
    singapore: { dialCode: "+65", flag: "🇸🇬" },
    nepal: { dialCode: "+977", flag: "🇳🇵" },
    bangladesh: { dialCode: "+880", flag: "🇧🇩" },
};

const initialForm: SignUpFormState = {
    fullName: "",
    dateOfBirth: "",
    invitationCode: "",
    gender: "",
    email: "",
    country: "",
    mobile: "",
    state: "",
    district: "",
    city: "",
    pincode: "",
    password: "",
    confirmPassword: "",
    termsAndConditions: false,
};

const SignUpPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const eighteenYearsAgo = useMemo(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 18);
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const [form, setForm] = useState<SignUpFormState>(initialForm);
    const [errors, setErrors] = useState<SignUpFormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const successToastShown = useRef(false);
    const errorToastShown = useRef<string | null>(null);

    const { authloading, autherror, success, user, message } = useSelector(
        (state: RootState) => state.customerAuth
    );
    const { countries, states, districts } = useSelector(
        (state: RootState) => state.location
    );

    // Fetch countries on load
    useEffect(() => {
        if (countries.length === 0) {
            dispatch(fetchAllCountries());
        }
    }, [countries.length, dispatch]);

    // Handle signup success
    useEffect(() => {
        if (user && success && !successToastShown.current) {
            successToastShown.current = true;
            if (message) toast.success(message, { toastId: "signup-success" });
            sessionStorage.setItem(LOCATION_GATE_SESSION_KEY, "1");
            dispatch(getProfile());
            dispatch(clearState());
            navigate("/HomePage");
        }
    }, [user, success, navigate, dispatch, message]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispatch(clearState());
        };
    }, [dispatch]);

    // Handle API errors
    useEffect(() => {
        if (autherror && errorToastShown.current !== autherror) {
            errorToastShown.current = autherror;
            setErrors((prev) => ({ ...prev, api: autherror }));
            toast.error(autherror, { toastId: "signup-error" });
        }
        if (!autherror) errorToastShown.current = null;
    }, [autherror]);

    // Country options derived from API with fallbacks
    const dynamicCountryOptions = useMemo<SelectOption[]>(() => {
        if (countries && countries.length > 0) {
            return countries.map((c: APICountry) => {
                const normalized = c.countryName.toLowerCase().trim();
                const meta = COUNTRY_DIAL_MAP[normalized];
                return {
                    value: c.countryName,
                    label: c.countryName,
                    flag: meta?.flag || "🌐",
                    dialCode: meta?.dialCode || "",
                };
            });
        }
        return defaultCountryOptions;
    }, [countries]);

    const isIndia = form.country?.toLowerCase().trim() === "india";

    const selectedCountry = useMemo(() => {
        const normalized = form.country?.toLowerCase().trim();
        return (
            dynamicCountryOptions.find(
                (c) =>
                    c.value.toLowerCase() === normalized ||
                    c.label.toLowerCase() === normalized
            ) ||
            COUNTRY_DIAL_MAP[normalized] || {
                dialCode: isIndia ? "+91" : "+--",
                flag: isIndia ? "🇮🇳" : "🌐",
            }
        );
    }, [form.country, dynamicCountryOptions, isIndia]);

    // State options from Redux
    const stateOptions = useMemo<SelectOption[]>(() => {
        if (!states || states.length === 0) return [];
        return states
            .map((s) => ({ value: s.stateName, label: s.stateName }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [states]);

    // District options from Redux
    const districtOptions = useMemo<SelectOption[]>(() => {
        if (!districts || districts.length === 0) return [];
        return districts
            .map((d) => ({ value: d.districtName, label: d.districtName }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [districts]);

    // Password strength check
    const passwordChecks = useMemo(
        () => ({
            length: form.password.length >= 8 && form.password.length <= 15,
            special: /[#?!@$%^&*-]/.test(form.password),
            case: /[a-z]/.test(form.password) && /[A-Z]/.test(form.password),
            number: /[0-9]/.test(form.password),
        }),
        [form.password]
    );

    const passwordScore = Object.values(passwordChecks).filter(Boolean).length;
    const passwordStrength = useMemo(() => {
        if (!form.password) {
            return "Create a strong password";
        }
        if (passwordScore <= 1) {
            return "Weak";
        }
        if (passwordScore === 2) {
            return "Fair";
        }
        if (passwordScore === 3) {
            return "Strong";
        }
        return "Excellent";
    }, [form.password, passwordScore]);

    const updateField = <K extends keyof SignUpFormState>(
        field: K,
        value: SignUpFormState[K]
    ) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: undefined, api: undefined }));
    };

    const handleCountryChange = (value: string) => {
        setForm((current) => ({
            ...current,
            country: value,
            state: "",
            district: "",
            city: "",
            mobile: "",
            pincode: "",
        }));
        setErrors((current) => ({
            ...current,
            country: undefined,
            state: undefined,
            district: undefined,
            city: undefined,
            mobile: undefined,
            pincode: undefined,
            api: undefined,
        }));
        dispatch(fetchStatesByCountryName(value));
    };

    const handleStateChange = (value: string) => {
        setForm((current) => ({
            ...current,
            state: value,
            district: "",
            city: "",
        }));
        setErrors((current) => ({
            ...current,
            state: undefined,
            district: undefined,
            city: undefined,
            api: undefined,
        }));
        if (value) {
            dispatch(fetchDistrictsByStateName(value));
        }
    };

    const handleDistrictChange = (value: string) => {
        setForm((current) => ({
            ...current,
            district: value,
        }));
        setErrors((current) => ({
            ...current,
            district: undefined,
            api: undefined,
        }));
    };

    const preventCopyPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
    };

    const validateForm = () => {
        const nextErrors: SignUpFormErrors = {};

        // 1. Full Name
        const trimmedName = form.fullName.trim();
        if (!trimmedName) {
            nextErrors.fullName = "Full Name is required";
        } else if (trimmedName.length > 40) {
            nextErrors.fullName = "Full Name cannot exceed 40 characters";
        } else if (/[^a-zA-Z\s]/.test(trimmedName)) {
            nextErrors.fullName = "Only alphabets and spaces are allowed";
        } else if (/\s\s/.test(trimmedName)) {
            nextErrors.fullName = "Double spaces are not allowed";
        }

        // 2. Gender
        if (!form.gender.trim()) {
            nextErrors.gender = "Gender is required";
        }

        // 3. Date of Birth
        if (!form.dateOfBirth) {
            nextErrors.dateOfBirth = "Date of Birth is required";
        } else {
            const [dy, dm, dd] = form.dateOfBirth.split("-").map(Number);
            const birthDate = new Date(dy, dm - 1, dd);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            if (age < 18) {
                nextErrors.dateOfBirth = "Minimum age must be 18 years";
            }
        }

        // 4. Email ID
        const trimmedEmail = form.email.trim();
        if (!trimmedEmail) {
            nextErrors.email = "Email is required";
        } else {
            const atCount = (trimmedEmail.match(/@/g) || []).length;
            const digitCount = (trimmedEmail.match(/\d/g) || []).length;
            if (atCount !== 1) {
                nextErrors.email = "Only one '@' allowed";
            } else {
                const [, domainPart] = trimmedEmail.split("@");
                const domainDotCount = (domainPart?.match(/\./g) || []).length;

                if (domainDotCount < 1 || domainDotCount > 2) {
                    nextErrors.email =
                        "Domain must have 1 or 2 dots (e.g. .com or .co.in)";
                } else if (trimmedEmail.length > 30) {
                    nextErrors.email = "Email cannot exceed 30 characters";
                } else if (digitCount > 6) {
                    nextErrors.email = "Email cannot contain more than 6 digits";
                } else if (
                    !/^[a-zA-Z0-9.]+@[a-zA-Z0-9]+(\.[a-zA-Z0-9]+){1,2}$/.test(
                        trimmedEmail
                    )
                ) {
                    nextErrors.email =
                        "Invalid email — only '@' and '.' allowed as special characters";
                }
            }
        }

        // 5. Country, State, District
        if (!form.country.trim()) nextErrors.country = "Country is required";
        if (!form.state.trim()) nextErrors.state = "State is required";
        if (!form.district.trim()) {
            nextErrors.district = "District is required";
        } else if (!isIndia) {
            if (/[^a-zA-Z0-9\s]/.test(form.district)) {
                nextErrors.district = "Only alphanumeric values allowed";
            } else if (form.district.length > 20) {
                nextErrors.district = "Max 20 characters allowed";
            }
        }

        // 6. Mobile No
        const trimmedMobile = form.mobile.trim();
        if (!trimmedMobile) {
            nextErrors.mobile = "Mobile Number is required";
        } else if (isIndia && !/^\d{10}$/.test(trimmedMobile)) {
            nextErrors.mobile = "Mobile Number must be 10 digits for India";
        } else if (!isIndia && !/^\d{8,12}$/.test(trimmedMobile)) {
            nextErrors.mobile = "Mobile Number must be between 8 to 12 digits";
        }

        // 7. City
        const trimmedCity = form.city.trim();
        if (!trimmedCity) {
            nextErrors.city = "City is required";
        } else if (/[^a-zA-Z\s]/.test(trimmedCity)) {
            nextErrors.city = "Only alphabets are allowed";
        } else if (trimmedCity.length > 25) {
            nextErrors.city = "Max 25 characters allowed";
        }

        // 8. Pincode
        const trimmedPincode = form.pincode.trim();
        if (!trimmedPincode) {
            nextErrors.pincode = "Pincode is required";
        } else if (trimmedPincode === "000000") {
            nextErrors.pincode = "Pincode 000000 does not exist";
        } else if (isIndia && !/^\d{6}$/.test(trimmedPincode)) {
            nextErrors.pincode = "Pincode must be 6 digits for India";
        }

        // 9. Password
        if (!form.password.trim()) {
            nextErrors.password = "Password is required";
        } else if (form.password.length < 8 || form.password.length > 15) {
            nextErrors.password = "Password must be 8-15 characters";
        } else if (
            !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-])/.test(
                form.password
            )
        ) {
            nextErrors.password =
                "Password must contain uppercase, lowercase, number, and special character";
        }

        // 10. Confirm Password
        if (!form.confirmPassword) {
            nextErrors.confirmPassword = "Confirm Password is required";
        } else if (form.password !== form.confirmPassword) {
            nextErrors.confirmPassword = "Passwords do not match";
        }

        // 11. Invitation Code (Optional)
        if (form.invitationCode.trim()) {
            const code = form.invitationCode.trim();
            if (!code.startsWith("S") && !code.startsWith("s")) {
                nextErrors.invitationCode = "Invitation code must start with 'S'";
            } else if (code.length > 11) {
                nextErrors.invitationCode =
                    "Invitation code cannot exceed 11 characters";
            } else if (/[^a-zA-Z0-9]/.test(code)) {
                nextErrors.invitationCode =
                    "Only alphanumeric characters allowed";
            } else {
                const extraAlphabets = (
                    code.slice(1).match(/[a-zA-Z]/g) || []
                ).length;
                if (extraAlphabets > 1) {
                    nextErrors.invitationCode =
                        "Only one alphabet letter (besides 'S') allowed";
                }
            }
        }

        // 12. Terms and Conditions
        if (!form.termsAndConditions) {
            nextErrors.termsAndConditions = "You must agree to the terms";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm() || authloading) {
            return;
        }

        const payload: RegisterRequest = {
            fullName: form.fullName.trim(),
            dob: form.dateOfBirth,
            gender: form.gender.toLowerCase().trim(),
            emailId: form.email.trim(),
            mobileNo: form.mobile.trim(),
            country: form.country.trim(),
            state: form.state.trim(),
            district: form.district.trim(),
            city: form.city.trim(),
            zipCode: form.pincode.trim(),
            password: form.password,
            termsAndConditions: form.termsAndConditions,
        };

        if (form.invitationCode && form.invitationCode.trim()) {
            payload.myReferralId = form.invitationCode.trim();
        }

        dispatch(register(payload));
    };

    return (
        <main className="signup-page">
            <div className="signup-shell">
                <section className="signup-left">
                    <div className="signup-left-content">
                        <button
                            type="button"
                            className="signup-brand"
                            onClick={() => navigate(signUpRoutes.home)}
                            aria-label="Go to Salar home"
                        >
                            <img
                                src="/assets/images/logos/salarlogo.png"
                                alt={signUpContent.brandAlt}
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).src =
                                        "./assets/img/salarlogo.png";
                                }}
                            />
                        </button>

                        <header className="signup-heading">
                            <h1> {signUpContent.title} </h1>
                            <p> {signUpContent.subtitle} </p>
                        </header>

                        <form
                            className="signup-form-card"
                            onSubmit={handleSubmit}
                            noValidate
                        >
                            {errors.api && (
                                <div className="signup-api-error">
                                    {errors.api}
                                </div>
                            )}

                            <div className="signup-form-grid">
                                {/* Full Name */}
                                <div className="signup-field-block signup-full">
                                    <input
                                        className={`signup-input ${
                                            errors.fullName ? "has-error" : ""
                                        }`}
                                        type="text"
                                        value={form.fullName}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(
                                                /[^a-zA-Z\s]/g,
                                                ""
                                            );
                                            val = val.replace(/^\s+/, "");
                                            val = val.replace(/\s\s+/g, " ");
                                            if (val.length > 40)
                                                val = val.slice(0, 40);
                                            updateField("fullName", val);
                                        }}
                                        onCopy={preventCopyPaste}
                                        onPaste={preventCopyPaste}
                                        maxLength={40}
                                        disabled={authloading}
                                        placeholder={
                                            signUpContent.fullNamePlaceholder
                                        }
                                        autoComplete="name"
                                    />
                                    {errors.fullName && (
                                        <span className="signup-error">
                                            {errors.fullName}
                                        </span>
                                    )}
                                </div>

                                {/* Date of Birth */}
                                <div className="signup-field-block">
                                    <SignUpDatePicker
                                        value={form.dateOfBirth}
                                        placeholder={
                                            signUpContent.dateOfBirthPlaceholder
                                        }
                                        hasError={Boolean(errors.dateOfBirth)}
                                        maxDate={eighteenYearsAgo}
                                        onChange={(value) =>
                                            updateField("dateOfBirth", value)
                                        }
                                    />
                                    {errors.dateOfBirth && (
                                        <span className="signup-error">
                                            {errors.dateOfBirth}
                                        </span>
                                    )}
                                </div>

                                {/* Gender */}
                                <div className="signup-field-block">
                                    <SignUpSelect
                                        value={form.gender}
                                        placeholder={
                                            signUpContent.genderPlaceholder
                                        }
                                        options={genderOptions}
                                        disabled={authloading}
                                        onChange={(value) =>
                                            updateField("gender", value)
                                        }
                                    />
                                    {errors.gender && (
                                        <span className="signup-error">
                                            {errors.gender}
                                        </span>
                                    )}
                                </div>

                                {/* Email ID */}
                                <div className="signup-field-block">
                                    <input
                                        className={`signup-input ${
                                            errors.email ? "has-error" : ""
                                        }`}
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => {
                                            let val = e.target.value
                                                .replace(/\s/g, "")
                                                .replace(/[^a-zA-Z0-9@.]/g, "");
                                            const atIdx = val.indexOf("@");
                                            if (
                                                atIdx !== -1 &&
                                                val.indexOf("@", atIdx + 1) !==
                                                    -1
                                            ) {
                                                const [local, ...rest] =
                                                    val.split("@");
                                                val =
                                                    local +
                                                    "@" +
                                                    rest.join("");
                                            }
                                            val = val.replace(/\.{2,}/g, ".");
                                            if (
                                                (val.match(/\d/g) || []).length >
                                                6
                                            )
                                                return;
                                            if (val.length > 30)
                                                val = val.slice(0, 30);
                                            updateField("email", val);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === " ")
                                                e.preventDefault();
                                        }}
                                        onBeforeInput={(e: any) => {
                                            if (
                                                e.data === ". " ||
                                                e.data === " "
                                            )
                                                e.preventDefault();
                                        }}
                                        onCopy={preventCopyPaste}
                                        onPaste={preventCopyPaste}
                                        inputMode="email"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        disabled={authloading}
                                        placeholder={
                                            signUpContent.emailPlaceholder
                                        }
                                        autoComplete="email"
                                    />
                                    {errors.email && (
                                        <span className="signup-error">
                                            {errors.email}
                                        </span>
                                    )}
                                </div>

                                {/* Invitation Code (Optional) */}
                                <div className="signup-field-block">
                                    <input
                                        className={`signup-input ${
                                            errors.invitationCode ? "has-error" : ""
                                        }`}
                                        type="text"
                                        value={form.invitationCode}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(
                                                /[^a-zA-Z0-9]/g,
                                                ""
                                            );
                                            if (
                                                val.length > 0 &&
                                                val[0].toUpperCase() !== "S"
                                            ) {
                                                val = "";
                                            } else if (val.length > 0) {
                                                const rest = val.slice(1);
                                                let alphaCount = 0;
                                                const filteredRest = rest.replace(
                                                    /[a-zA-Z]/g,
                                                    (char) => {
                                                        if (alphaCount < 1) {
                                                            alphaCount++;
                                                            return char;
                                                        }
                                                        return "";
                                                    }
                                                );
                                                val = "S" + filteredRest;
                                            }
                                            if (val.length > 11)
                                                val = val.slice(0, 11);
                                            updateField("invitationCode", val);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === " ")
                                                e.preventDefault();
                                        }}
                                        disabled={authloading}
                                        placeholder={
                                            signUpContent.invitationCodePlaceholder
                                        }
                                    />
                                    {errors.invitationCode && (
                                        <span className="signup-error">
                                            {errors.invitationCode}
                                        </span>
                                    )}
                                </div>

                                <div className="signup-field-divider signup-full">
                                    <span />
                                </div>

                                {/* Country */}
                                <div className="signup-field-block">
                                    <SignUpSelect
                                        value={form.country}
                                        placeholder={
                                            signUpContent.countryPlaceholder
                                        }
                                        options={dynamicCountryOptions}
                                        showDialCode
                                        searchable
                                        disabled={authloading}
                                        onChange={handleCountryChange}
                                    />
                                    {errors.country && (
                                        <span className="signup-error">
                                            {errors.country}
                                        </span>
                                    )}
                                </div>

                                {/* Mobile */}
                                <div className="signup-field-block">
                                    <div
                                        className={`signup-phone-field ${
                                            errors.mobile ? "has-error" : ""
                                        }`}
                                    >
                                        <span className="signup-phone-code">
                                            <b>
                                                {selectedCountry?.flag || "🌐"}
                                            </b>
                                            <strong>
                                                {selectedCountry?.dialCode ||
                                                    "+--"}
                                            </strong>
                                        </span>

                                        <input
                                            type="tel"
                                            value={form.mobile}
                                            onChange={(e) => {
                                                if (!form.country) {
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        mobile: "Please select a country first",
                                                    }));
                                                    return;
                                                }
                                                let val = e.target.value.replace(
                                                    /[^0-9]/g,
                                                    ""
                                                );
                                                if (isIndia && val.length > 10)
                                                    val = val.slice(0, 10);
                                                else if (!isIndia && val.length > 12)
                                                    val = val.slice(0, 12);
                                                updateField("mobile", val);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === " ")
                                                    e.preventDefault();
                                            }}
                                            onCopy={preventCopyPaste}
                                            onPaste={preventCopyPaste}
                                            disabled={
                                                authloading || !form.country
                                            }
                                            placeholder={
                                                form.country
                                                    ? signUpContent.mobilePlaceholder
                                                    : "Select country first for mobile"
                                            }
                                            autoComplete="tel"
                                        />
                                    </div>
                                    {errors.mobile && (
                                        <span className="signup-error">
                                            {errors.mobile}
                                        </span>
                                    )}
                                </div>

                                {/* State */}
                                <div className="signup-field-block">
                                    <SignUpSelect
                                        value={form.state}
                                        placeholder={
                                            signUpContent.statePlaceholder
                                        }
                                        options={stateOptions}
                                        searchable
                                        disabled={
                                            authloading || !form.country
                                        }
                                        onChange={handleStateChange}
                                    />
                                    {errors.state && (
                                        <span className="signup-error">
                                            {errors.state}
                                        </span>
                                    )}
                                </div>

                                {/* District */}
                                <div className="signup-field-block">
                                    {isIndia ? (
                                        <SignUpSelect
                                            value={form.district}
                                            placeholder={
                                                signUpContent.districtPlaceholder
                                            }
                                            options={districtOptions}
                                            searchable
                                            disabled={
                                                authloading || !form.state
                                            }
                                            onChange={handleDistrictChange}
                                        />
                                    ) : (
                                        <input
                                            className={`signup-input ${
                                                errors.district ? "has-error" : ""
                                            }`}
                                            type="text"
                                            value={form.district}
                                            onChange={(e) => {
                                                let val = e.target.value.replace(
                                                    /[^a-zA-Z0-9\s]/g,
                                                    ""
                                                );
                                                if (val.length > 20)
                                                    val = val.slice(0, 20);
                                                updateField("district", val);
                                            }}
                                            disabled={authloading}
                                            placeholder="Enter District"
                                            maxLength={20}
                                        />
                                    )}
                                    {errors.district && (
                                        <span className="signup-error">
                                            {errors.district}
                                        </span>
                                    )}
                                </div>

                                {/* City */}
                                <div className="signup-field-block">
                                    <input
                                        className={`signup-input ${
                                            errors.city ? "has-error" : ""
                                        }`}
                                        type="text"
                                        value={form.city}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(
                                                /[^a-zA-Z\s]/g,
                                                ""
                                            );
                                            if (val.length > 25)
                                                val = val.slice(0, 25);
                                            updateField("city", val);
                                        }}
                                        disabled={authloading}
                                        placeholder={
                                            signUpContent.cityPlaceholder
                                        }
                                        autoComplete="address-level2"
                                        maxLength={25}
                                    />
                                    {errors.city && (
                                        <span className="signup-error">
                                            {errors.city}
                                        </span>
                                    )}
                                </div>

                                {/* Pincode */}
                                <div className="signup-field-block">
                                    <input
                                        className={`signup-input ${
                                            errors.pincode ? "has-error" : ""
                                        }`}
                                        type="text"
                                        value={form.pincode}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(
                                                /[^0-9]/g,
                                                ""
                                            );
                                            if (isIndia && val.length > 6)
                                                val = val.slice(0, 6);
                                            else if (!isIndia && val.length > 10)
                                                val = val.slice(0, 10);
                                            updateField("pincode", val);
                                        }}
                                        disabled={authloading}
                                        placeholder={
                                            signUpContent.pincodePlaceholder
                                        }
                                        autoComplete="postal-code"
                                    />
                                    {errors.pincode && (
                                        <span className="signup-error">
                                            {errors.pincode}
                                        </span>
                                    )}
                                </div>

                                <div className="signup-field-divider signup-full">
                                    <span />
                                </div>

                                {/* Password */}
                                <div className="signup-field-block">
                                    <div
                                        className={`signup-password-field ${
                                            errors.password ? "has-error" : ""
                                        }`}
                                    >
                                        <input
                                            type={
                                                showPassword ? "text" : "password"
                                            }
                                            value={form.password}
                                            onChange={(e) => {
                                                let val = e.target.value.replace(
                                                    /\s/g,
                                                    ""
                                                );
                                                if (val.length > 15)
                                                    val = val.slice(0, 15);
                                                updateField("password", val);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === " ")
                                                    e.preventDefault();
                                            }}
                                            onBeforeInput={(e: any) => {
                                                if (
                                                    e.data === ". " ||
                                                    e.data === " "
                                                )
                                                    e.preventDefault();
                                            }}
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            maxLength={15}
                                            disabled={authloading}
                                            placeholder={
                                                signUpContent.passwordPlaceholder
                                            }
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword(
                                                    (current) => !current
                                                )
                                            }
                                            aria-label={
                                                showPassword
                                                    ? "Hide password"
                                                    : "Show password"
                                            }
                                        >
                                            {showPassword ? (
                                                <EyeOff size={17} />
                                            ) : (
                                                <Eye size={17} />
                                            )}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <span className="signup-error">
                                            {errors.password}
                                        </span>
                                    )}
                                </div>

                                {/* Confirm Password */}
                                <div className="signup-field-block">
                                    <div
                                        className={`signup-password-field ${
                                            errors.confirmPassword
                                                ? "has-error"
                                                : ""
                                        }`}
                                    >
                                        <input
                                            type={
                                                showConfirmPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            value={form.confirmPassword}
                                            onChange={(e) => {
                                                let val = e.target.value.replace(
                                                    /\s/g,
                                                    ""
                                                );
                                                if (val.length > 15)
                                                    val = val.slice(0, 15);
                                                updateField(
                                                    "confirmPassword",
                                                    val
                                                );
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === " ")
                                                    e.preventDefault();
                                            }}
                                            onBeforeInput={(e: any) => {
                                                if (
                                                    e.data === ". " ||
                                                    e.data === " "
                                                )
                                                    e.preventDefault();
                                            }}
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            maxLength={15}
                                            disabled={authloading}
                                            placeholder={
                                                signUpContent.confirmPasswordPlaceholder
                                            }
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowConfirmPassword(
                                                    (current) => !current
                                                )
                                            }
                                            aria-label={
                                                showConfirmPassword
                                                    ? "Hide confirm password"
                                                    : "Show confirm password"
                                            }
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff size={17} />
                                            ) : (
                                                <Eye size={17} />
                                            )}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <span className="signup-error">
                                            {errors.confirmPassword}
                                        </span>
                                    )}
                                </div>

                                {/* Password Guide */}
                                <div className="signup-password-guide signup-full">
                                    <div className="signup-password-summary">
                                        <div>
                                            <span className="signup-shield-icon">
                                                <ShieldCheck
                                                    size={18}
                                                    strokeWidth={1.9}
                                                />
                                            </span>
                                            <div>
                                                <strong>Password strength</strong>
                                                <small>
                                                    {passwordStrength}
                                                </small>
                                            </div>
                                        </div>

                                        <div
                                            className="signup-strength-bars"
                                            aria-label={`Password strength: ${passwordStrength}`}
                                        >
                                            {[1, 2, 3, 4].map((level) => (
                                                <span
                                                    key={level}
                                                    className={
                                                        passwordScore >= level
                                                            ? "is-active"
                                                            : ""
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="signup-password-rules">
                                        {passwordRules.map((rule) => {
                                            const isValid =
                                                passwordChecks[rule.id];
                                            return (
                                                <div
                                                    key={rule.id}
                                                    className={
                                                        isValid ? "is-valid" : ""
                                                    }
                                                >
                                                    <CheckCircle2
                                                        size={14}
                                                        strokeWidth={2}
                                                    />
                                                    <span>{rule.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Terms & Conditions */}
                                <div className="signup-terms-block">
                                    <label className="signup-terms-label">
                                        <input
                                            type="checkbox"
                                            className="signup-terms-checkbox"
                                            checked={form.termsAndConditions}
                                            onChange={(e) =>
                                                updateField(
                                                    "termsAndConditions",
                                                    e.target.checked
                                                )
                                            }
                                            disabled={authloading}
                                        />
                                        <span>
                                            I agree to the{" "}
                                            <a
                                                href="/termsofuse"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Terms and Conditions
                                            </a>
                                        </span>
                                    </label>
                                    {errors.termsAndConditions && (
                                        <span className="signup-error">
                                            {errors.termsAndConditions}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="signup-actions">
                                <button
                                    type="submit"
                                    className="signup-submit"
                                    disabled={authloading}
                                >
                                    {authloading
                                        ? signUpContent.signingUpLabel
                                        : signUpContent.signUpLabel}
                                </button>

                                <div className="signup-signin-row">
                                    <span>{signUpContent.signInPrompt}</span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(signUpRoutes.signIn)
                                        }
                                    >
                                        {signUpContent.signInLabel}
                                    </button>
                                </div>
                            </div>
                        </form>

                        <div className="signup-social">
                            <span>{signUpContent.socialTitle}</span>
                            <div className="signup-social-row">
                                {signUpSocialLinks.map((social) => (
                                    <a
                                        key={social.id}
                                        href={social.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={social.label}
                                        title={social.label}
                                    >
                                        <i className={social.iconClass} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section
                    className="signup-right"
                    aria-label="Salar shopping"
                >
                    <div className="signup-visual">
                        <img
                            src="/assets/img/register/signup.jpg"
                            alt={signUpContent.heroAlt}
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                    "../../assets/images/images/signup.jpg";
                            }}
                        />
                        <div className="signup-visual-overlay" />
                        <span className="signup-visual-orbit signup-visual-orbit-one" />
                        <span className="signup-visual-orbit signup-visual-orbit-two" />
                        <span className="signup-visual-shine" />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default SignUpPage;
